import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { UrlKey, UrlService } from '../services/url/url.service';
import { ENVIRONMENT_CONFIG, EnvironmentConfig } from '../environment.config';
import { WebLogger } from '../web-logger/WebLogger';

@Injectable()
export class KongInterceptor implements HttpInterceptor {
  private rknEnabled$: Observable<boolean> | null = null;
  private rkeEnabled$: Observable<boolean> | null = null;

  constructor(
    private httpClient: HttpClient,
    private urlService: UrlService,
    @Inject(ENVIRONMENT_CONFIG) private config: EnvironmentConfig
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (request.url.includes('gatekeeper')) {
      return next.handle(request);
    }

    // Internal (Okta) requests carry Authorization header → RKN path
    if (request.headers.has('Authorization')) {
      return this.handleRkn(request, next);
    }

    // External (Siteminder) requests carry X-Url-To-Forward header → RKE path
    if (request.headers.has('X-Url-To-Forward')) {
      return this.handleRke(request, next);
    }

    return next.handle(request);
  }

  private handleRkn(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return this.getRknEnabled().pipe(
      switchMap(enabled => {
        if (!enabled || !this.isWEPSUrl(request.url)) {
          return next.handle(request);
        }

        const parsed = new URL(request.url);
        const rknBaseUrl = this.urlService.getUrl(UrlKey.rkn_base_url);
        const rknFullUrl = `${rknBaseUrl}${parsed.pathname}${parsed.search}`;

        return next.handle(request.clone({
          url: rknFullUrl,
          headers: request.headers.delete('Authorization')
        })).pipe(
          catchError((error: unknown) => {
            if (error instanceof HttpErrorResponse && error.status === 401) {
              const authUrl = `${rknBaseUrl}/auth?logonTargetUrl=${encodeURIComponent(window.location.href)}`;
              window.location.href = authUrl;
              return throwError(() => error);
            }
            return throwError(() => error);
          })
        );
      })
    );
  }

  private handleRke(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return this.getRkeEnabled().pipe(
      switchMap(enabled => {
        if (!enabled || !this.isWEPSProxyUrl(request.url)) {
          return next.handle(request);
        }

        const forwardUrl = request.headers.get('X-Url-To-Forward')!;
        const parsed = new URL(forwardUrl);
        const rkeBaseUrl = this.urlService.getUrl(UrlKey.rke_base_url);
        const rkeFullUrl = `${rkeBaseUrl}${parsed.pathname}${parsed.search}`;

        return next.handle(request.clone({
          url: rkeFullUrl,
          headers: request.headers
            .delete('X-Url-To-Forward')
            .delete('SM_AUTHDIRNAME')
            .set('X-XSRF-TOKEN', this.getXsrfToken())
        })).pipe(
          catchError((error: unknown) => {
            if (error instanceof HttpErrorResponse && error.status === 401) {
              const rkeLogonUrl = this.urlService.getUrl(UrlKey.rke_logon_url);
              const targetOverride = encodeURIComponent(window.location.href);
              window.location.href = `${rkeLogonUrl}?TARGET_OVERRIDE=${targetOverride}&authenticate=true`;
              return throwError(() => error);
            }
            return throwError(() => error);
          })
        );
      })
    );
  }

  private getXsrfToken(): string {
    const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]*)/);
    return match ? decodeURIComponent(match[1]) : '';
  }

  private isWEPSUrl(url: string): boolean {
    try {
      const { hostname } = new URL(url);
      return /^wealth-management\.retailadvice1[pte]\.c1\.vanguard\.com$/.test(hostname);
    } catch {
      return false;
    }
  }

  private isWEPSProxyUrl(url: string): boolean {
    try {
      const { hostname, pathname } = new URL(url);
      return /^(dev|sat)?personal1\.vanguard\.com$/.test(hostname) && pathname.startsWith('/wah-wah-proxy/request');
    } catch {
      return false;
    }
  }

  private getRknEnabled(): Observable<boolean> {
    if (!this.rknEnabled$) {
      const gatekeeperUrl = this.urlService.getUrl(UrlKey.gatekeeeper_url, {
        featureIds: 'rkn-enabled',
        env: this.config.gatekeeper_env
      });

      this.rknEnabled$ = this.httpClient.get<{ featureId: string; enabled: boolean }[]>(gatekeeperUrl).pipe(
        map(features => features.find(f => f.featureId === 'rkn-enabled')?.enabled ?? false),
        catchError(() => {
          // TODO: Change the return to true once RKN is fully enabled in prod
          WebLogger.log({ message: 'RKN gatekeeper fetch failed, defaulting to disabled', level: 'warn' });
          return of(false);
        }),
        shareReplay(1)
      );
    }

    return this.rknEnabled$;
  }

  private getRkeEnabled(): Observable<boolean> {
    if (!this.rkeEnabled$) {
      const gatekeeperUrl = this.urlService.getUrl(UrlKey.gatekeeeper_url, {
        featureIds: 'rke-enabled',
        env: this.config.gatekeeper_env
      });

      this.rkeEnabled$ = this.httpClient.get<{ featureId: string; enabled: boolean }[]>(gatekeeperUrl).pipe(
        map(features => features.find(f => f.featureId === 'rke-enabled')?.enabled ?? false),
        catchError(() => {
          // TODO: Change the return to true once RKE is fully enabled in prod
          WebLogger.log({ message: 'RKE gatekeeper fetch failed, defaulting to disabled', level: 'warn' });
          return of(false);
        }),
        shareReplay(1)
      );
    }

    return this.rkeEnabled$;
  }
}
